import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  Optional,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '@univacity/angular/datas';
import { RequiredActionAlias, RoutePath } from '@univacity/shared/types';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFacebookSquare, faGoogle } from '@fortawesome/free-brands-svg-icons';
import {
  AngularUiModule,
  CustomValidators,
  DestroyAbleComponent,
  FormUtil,
} from '@univacity/angular/ui';
import { ApiError } from '@univacity/shared/types';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'un-login-form',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    AngularUiModule,
    FontAwesomeModule,
    RecaptchaModule,
  ],
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent extends DestroyAbleComponent implements OnInit {
  @Input() returnUrl?: string;
  form: FormGroup;
  registerLink?: string | string[] = [];
  forgotPasswordLink?: string | string[] = [];
  faFacebook = faFacebookSquare;
  faGoogle = faGoogle;
  recaptchaKey = environment.recaptchaKey;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
    private cdr: ChangeDetectorRef,
    @Optional()
    @Inject(NZ_MODAL_DATA)
    private nzModalData?: { returnUrl?: string },
    @Optional() public modalRef?: NzModalRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.returnUrl ??=
      this.nzModalData?.returnUrl ||
      this.route.snapshot.queryParams['returnUrl'];
    if (!this.modalRef) {
      this.registerLink = RoutePath.authRegister;
      this.forgotPasswordLink = RoutePath.authForgotPassword;
    }

    if (this.auth.isLoggedIn) {
      if (this.modalRef) this.modalRef.close();
      else this.router.navigate([this.returnUrl || RoutePath.dashboard]);
    }

    this.initForm();
    this.cdr.detectChanges();
  }

  initForm() {
    this.form = this.fb.group({
      email: ['', [Validators.required, CustomValidators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      returnUrl: [this.returnUrl],
      recaptcha: ['', [Validators.required]],
    });
  }

  oauth(provider: 'facebook' | 'google' | 'keycloak') {
    window.location.href = `${
      environment.mainServiceEndpoint
    }/auth/${provider}?redirect=${
      this.returnUrl
        ? window.location.origin + this.returnUrl
        : window.location.href
    }`;
  }

  login() {
    FormUtil.updateFormValidity(this.form);
    if (this.form.get('recaptcha')?.invalid)
      this.message.error($localize`Solve Recaptca`);

    if (this.form.valid) {
      this.auth
        .signIn(this.form.value, this.form.value.recaptcha)
        .pipe(takeUntil(this.dead$))
        .subscribe({
          next: () => {
            if (this.modalRef) this.modalRef?.close();
            else this.router.navigate([this.returnUrl || RoutePath.dashboard]);
          },
          error: (
            err?: ApiError & {
              response: { requiredActions: RequiredActionAlias[] };
            }
          ) => {
            err?.message &&
              this.message.error(err.message, { nzDuration: 10000 });
            if (err?.response?.requiredActions?.length) {
              this.modalRef?.close();
            }
          },
        });
    }
  }
  resolveRecaptcha(recaptcha?: string | null) {
    this.form.patchValue({ recaptcha });
  }
}
